import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "channel" }
const _hoisted_2 = ["src"]
const _hoisted_3 = { class: "text" }

import { PropType, onMounted, onUnmounted, ref } from 'vue';
import { useOnline } from '@vueuse/core';
import { Row, Col, Radio, message, Button } from 'ant-design-vue';
import { getOrderPayElement, getOrderStatus, getContractStatus, Status } from '@/apis';
import { ISUBMIT_SOURCE, ORDER_STATUS } from '@/apis/common';
import {
  getWechatOpenId,
  hasPendingWechatAuthorization,
  invokeWechatJsapiPay,
  isWechatBrowser,
  waitForWechatBridge,
} from '@/utils/wechat';
import { debugWechatJsapi, getWechatDebugError } from '@/utils/wechatDebug';
import { channelList, IChannelItem, CHANNEL, ORDER_PAGE_STATUS, OrderInfo } from '../utils/config';
import { handleResultStatus } from '../utils/method';


export default /*@__PURE__*/_defineComponent({
  __name: 'Unpaid',
  props: {
  orderData: {
    type: Object as PropType<OrderInfo>,
    required: true,
  },

  isTimeout: {
    type: Boolean,
    required: false,
    default: false,
  },

  changePaymentStatus: {
    type: Function as PropType<(status: ORDER_PAGE_STATUS) => void>,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const {
  orderData: { orderNo, succRedirect, contractNo, failRedirect, channel, appKey, appId },
} = props;
const currentChannelList = ref<IChannelItem[]>([]);
const channelId = ref<CHANNEL>();
const loadingPaid = ref<boolean>(false);
const isJsapiPaymentPending = ref<boolean>(false);
const isNormalOrder = Boolean(orderNo && orderNo !== 'null');
const isWechatPaymentLocked = Boolean(
  isNormalOrder &&
    isWechatBrowser() &&
    channel?.some((item) => item.channelCode === CHANNEL.WECHAT),
);
let timer: number | undefined;
let resultRedirectTimer: number | undefined;

const stopPolling = () => {
  if (timer !== undefined) {
    window.clearInterval(timer);
    timer = undefined;
  }
};

// 轮询查订单状态，根据支付状态跳转到指定页面
const getOrderStatusFn = async (showJsapiDebug = false): Promise<boolean> => {
  try {
    const { data } = await getOrderStatus({
      orderNo,
    });
    if (data) {
      if (showJsapiDebug) {
        debugWechatJsapi(
          `步骤 11/11：查单完成，orderStatus=${data.orderStatus}，paymentStatus=${data.paymentStatus}`,
        );
      }

      if (data?.orderStatus === ORDER_STATUS.PAID) {
        stopPolling();
        message.success('订单已支付');
        resultRedirectTimer = window.setTimeout(() => {
          handleResultStatus(succRedirect);
        }, 500);
        return true;
      }

      if (data.orderStatus === ORDER_STATUS.CANCELLED) {
        message.info('订单已取消');
        stopPolling();
        return false;
      }

      // JSAPI 已调起时需要等待支付结果，不能由轮询提前解锁重复支付。
      if (!props.isTimeout && !isJsapiPaymentPending.value) {
        loadingPaid.value = false;
      }
    }
  } catch (error) {
    if (showJsapiDebug) {
      debugWechatJsapi(`步骤 11/11：查单失败，${getWechatDebugError(error)}`);
    }
    // JSAPI 成功后的短暂查单失败不应停止后续轮询确认。
    if (!isJsapiPaymentPending.value) {
      stopPolling();
    }
  }

  return false;
};

// 请求查询签约状态
const getContractStatusFn = async () => {
  try {
    const { data } = await getContractStatus({
      contractNo,
    });
    if (data) {
      if (data?.status === Status.SIGNED || data?.status === Status.EFFECTIVE) {
        message.success('已成功签约');
        resultRedirectTimer = window.setTimeout(() => {
          handleResultStatus(succRedirect);
        }, 500);
      } else if (data?.status === Status.PRE_SIGNING) {
        // 正常预签约状态：只有在未超时的情况下才重置 loading
        if (!props.isTimeout) {
          loadingPaid.value = false;
        }
      }
    }
  } catch (error) {
    console.error(error);
  }
};

timer = window.setInterval(orderNo !== 'null' ? getOrderStatusFn : getContractStatusFn, 2000);

// 切换渠道
const changeChannel = (val: CHANNEL) => {
  channelId.value = val;
  loadingPaid.value = false;
};

// 容错处理：接口报错 / 异常 / 无网络等情况
const handleOrderErrorStatus = () => {
  stopPolling();
  const online = useOnline();
  if (online.value) {
    // 跳失败页面
    message.error('当前订单已取消无法继续支付，请重新下单！');
    handleResultStatus(failRedirect);
  } else {
    //  error 页面
    message.error('网络异常，请检查网络状态');
  }
};

const handlePayElementError = (error: any, isJsapiRequest = false) => {
  loadingPaid.value = false;
  if (isJsapiRequest) {
    isJsapiPaymentPending.value = false;
    debugWechatJsapi(`步骤 9/11：获取 JSAPI 支付要素失败，${getWechatDebugError(error)}`);
  }

  if (error.code === 20001) {
    handleResultStatus(succRedirect);
  } else if (error.code === 20002) {
    handleResultStatus(failRedirect);
  } else {
    handleOrderErrorStatus();
  }
};

const handleWechatJsapiPay = async () => {
  const describePaymentValue = (value: unknown): string => {
    if (typeof value !== 'string') {
      return `type=${value === null ? 'null' : typeof value}`;
    }

    return value ? `string(length=${value.length})` : 'empty string';
  };
  const getPaySignFingerprint = async (paySign: unknown): Promise<string> => {
    if (typeof paySign !== 'string' || !paySign) return 'unavailable';

    try {
      if (!window.crypto?.subtle) return `unavailable(length=${paySign.length})`;

      const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(paySign));
      return Array.from(new Uint8Array(hash))
        .slice(0, 8)
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('');
    } catch {
      return `unavailable(length=${paySign.length})`;
    }
  };
  let openId: string | null;

  try {
    debugWechatJsapi('步骤 2/11：开始获取当前微信账号的 OpenID');
    openId = await getWechatOpenId({
      orderNo,
      appKey,
      appId,
    });
  } catch (error: any) {
    isJsapiPaymentPending.value = false;
    loadingPaid.value = false;
    debugWechatJsapi(`OpenID 获取流程异常，${getWechatDebugError(error)}`);
    message.error(error?.message || '获取微信授权信息失败，请重试');
    return;
  }

  // 首次支付会跳转到微信授权页，当前页面无需继续执行。
  if (!openId) {
    debugWechatJsapi('步骤 3/11：已发起微信 OAuth 跳转，等待微信回调');
    return;
  }

  debugWechatJsapi('步骤 6/11：已获取 OpenID');

  try {
    await waitForWechatBridge();
  } catch (error: any) {
    isJsapiPaymentPending.value = false;
    loadingPaid.value = false;
    debugWechatJsapi(`步骤 8/11：Bridge 初始化失败，${getWechatDebugError(error)}`);
    message.error(error?.message || '微信支付环境未准备完成，请重试');
    return;
  }

  let data;
  const payElementParams = {
    submitSource: ISUBMIT_SOURCE.JSAPI,
    channel: CHANNEL.WECHAT,
    orderNo,
    appKey,
    wxOpenId: openId,
    returnUrl: window.location.href,
  };
  try {
    debugWechatJsapi(
      `步骤 9/11：请求 JSAPI 支付要素，submitSource=${payElementParams.submitSource}，channel=${
        payElementParams.channel
      }，orderNoLength=${orderNo.length}，hasOpenId=${Boolean(openId)}，returnUrlOrigin=${
        window.location.origin
      }`,
    );
    ({ data } = await getOrderPayElement(payElementParams));
  } catch (error: any) {
    handlePayElementError(error, true);
    return;
  }

  const channelFactor = data?.channelFactor;
  const packageValue =
    channelFactor?.package ||
    channelFactor?.packageInfo ||
    (channelFactor?.prepayid ? `prepay_id=${channelFactor.prepayid}` : '');
  const timeStampValue = channelFactor?.timeStamp || channelFactor?.timestamp;
  const paySignValue = channelFactor?.sign || channelFactor?.paySign;
  const paymentFactorFields = [
    { name: 'appId', value: channelFactor?.appId },
    { name: 'timeStamp', value: timeStampValue },
    { name: 'nonceStr', value: channelFactor?.nonceStr },
    { name: 'package', value: packageValue },
    { name: 'sign', value: paySignValue },
  ];
  const missingPaymentFields = paymentFactorFields
    .filter((field) => !field.value)
    .map((field) => field.name);
  const paySignFingerprint = await getPaySignFingerprint(paySignValue);
  const packageSource = channelFactor?.package
    ? 'package'
    : channelFactor?.packageInfo
    ? 'packageInfo'
    : channelFactor?.prepayid
    ? 'prepayid'
    : 'missing';
  const timeStampSource = channelFactor?.timeStamp
    ? 'timeStamp'
    : channelFactor?.timestamp
    ? 'timestamp'
    : 'missing';
  const signSource = channelFactor?.sign
    ? 'sign'
    : channelFactor?.paySign
    ? 'paySign'
    : 'missing';
  const packageFormat =
    typeof packageValue === 'string' && packageValue.startsWith('prepay_id=')
      ? 'prepay_id'
      : 'unexpected';
  const timeStampFormat =
    typeof timeStampValue === 'string' && /^\d{10}$/.test(timeStampValue)
      ? 'unix-seconds'
      : 'unexpected';
  const paymentFactorSummary = paymentFactorFields
    .map((field) => `${field.name}=${describePaymentValue(field.value)}`)
    .join('，');
  debugWechatJsapi(
    `步骤 9/11：支付要素诊断，${paymentFactorSummary}，packageSource=${packageSource}，timeStampSource=${timeStampSource}，signSource=${signSource}，packageFormat=${packageFormat}，timeStampFormat=${timeStampFormat}，signTypeSource=${
      channelFactor?.signType ? '服务端返回' : '前端默认 MD5'
    }，signType=${channelFactor?.signType || 'MD5'}，paySignFingerprint=${paySignFingerprint}`,
  );

  if (missingPaymentFields.length > 0) {
    isJsapiPaymentPending.value = false;
    loadingPaid.value = false;
    debugWechatJsapi(
      `步骤 9/11：支付要素不完整，缺失字段=${missingPaymentFields.join(
        '、',
      )}，未调用 WeixinJSBridge`,
    );
    message.error(`获取微信支付参数失败，缺少 ${missingPaymentFields.join('、')}`);
    return;
  }

  const paymentParams = {
    appId: channelFactor.appId,
    timeStamp: timeStampValue,
    nonceStr: channelFactor.nonceStr,
    package: packageValue,
    signType: channelFactor.signType || 'MD5',
    paySign: paySignValue,
  };
  debugWechatJsapi(
    `步骤 10/11：调用微信支付，signType=${paymentParams.signType}，paySignFingerprint=${paySignFingerprint}`,
  );

  const paymentResult = await invokeWechatJsapiPay(paymentParams);
  const signatureVerificationFailed =
    paymentResult.status === 'fail' && /签名|signature|sign/i.test(paymentResult.message);
  debugWechatJsapi(
    `步骤 10/11：微信支付结果，status=${paymentResult.status}，result=${
      paymentResult.status === 'success' ? 'ok' : paymentResult.message
    }，signatureVerificationFailed=${signatureVerificationFailed}`,
  );

  if (paymentResult.status === 'success') {
    const isPaid = await getOrderStatusFn(true);
    if (!isPaid) {
      debugWechatJsapi('步骤 11/11：微信回调成功，但订单尚未更新，继续轮询确认');
      message.info('支付结果确认中，请勿重复支付');
    }
    return;
  }

  const isPaid = await getOrderStatusFn(true);
  if (isPaid) return;

  isJsapiPaymentPending.value = false;
  if (paymentResult.status === 'cancel') {
    message.info(paymentResult.message);
  } else if (signatureVerificationFailed) {
    message.error('微信支付签名校验失败，请联系商户核对支付要素和签名配置');
  } else {
    message.error(paymentResult.message);
  }

  if (!props.isTimeout) {
    loadingPaid.value = false;
  }
};

// 根据订单信息和选择渠道去获取跳转链接
const toPay = async () => {
  // ✅ 如果已超时，阻止支付
  if (props.isTimeout) {
    message.warning('订单已超时，无法支付');
    return;
  }

  loadingPaid.value = true;

  // 普通订单在微信内置浏览器中，使用服务号 JSAPI 支付。
  if (isNormalOrder && channelId.value === CHANNEL.WECHAT && isWechatBrowser()) {
    debugWechatJsapi('步骤 1/11：命中微信内置浏览器普通订单 JSAPI 分支');
    isJsapiPaymentPending.value = true;
    await handleWechatJsapiPay();
    return;
  }

  if (isNormalOrder && channelId.value === CHANNEL.WECHAT) {
    debugWechatJsapi('步骤 1/11：当前不是微信内置浏览器，保持原微信 H5 codeUrl 支付');
  }

  const currentUrl = window.location.href;
  const encodeUrl = encodeURIComponent(currentUrl);
  const params = {
    channel: channelId.value,
    orderNo,
    contractNo,
    submitSource: ISUBMIT_SOURCE.H5,
    returnUrl: currentUrl,
  };

  try {
    const { data } = await getOrderPayElement(params);
    if (data) {
      const {
        channelFactor: { codeUrl },
      } = data;
      // 跳转中间页发起支付(微信需要拼接redirect_url)
      const redirectUrl =
        channelId.value === CHANNEL.WECHAT ? `${codeUrl}&redirect_url=${encodeUrl}` : codeUrl;
      window.location.href = redirectUrl;
      // 跳转成功后页面会刷新，不需要重置 loading
    } else {
      // 处理 data 为空的情况
      loadingPaid.value = false;
      message.error('获取支付信息失败，请重试');
    }
  } catch (error: any) {
    handlePayElementError(error);
  }
};

onMounted(async () => {
  // 组装渠道列表
  currentChannelList.value = channelList.filter((item1) => {
    return channel?.some((item2) => item2.channelCode === item1.id);
  });

  // 微信环境内固定使用微信支付，其他环境保持原有默认渠道。
  if (isWechatPaymentLocked) {
    channelId.value = CHANNEL.WECHAT;
  } else if (currentChannelList.value.length > 0) {
    channelId.value = currentChannelList.value[0].id;
  }

  const supportsWechat = currentChannelList.value.some((item) => item.id === CHANNEL.WECHAT);
  if (
    isNormalOrder &&
    supportsWechat &&
    isWechatBrowser() &&
    hasPendingWechatAuthorization({ orderNo, appKey, appId })
  ) {
    debugWechatJsapi('步骤 4/11：OAuth 回跳校验通过，自动续跑微信 JSAPI 支付');
    channelId.value = CHANNEL.WECHAT;
    loadingPaid.value = true;
    isJsapiPaymentPending.value = true;
    await toPay();
  }
});

onUnmounted(() => {
  stopPolling();
  if (resultRedirectTimer !== undefined) {
    window.clearTimeout(resultRedirectTimer);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_unref(isWechatPaymentLocked))
      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(currentChannelList.value, (item, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "channel-item"
          }, [
            _createVNode(_unref(Row), {
              justify: "space-between",
              onClick: ($event: any) => (changeChannel(item.id))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Col), null, {
                  default: _withCtx(() => [
                    _createElementVNode("img", {
                      src: item?.img,
                      class: "pay-icon"
                    }, null, 8, _hoisted_2),
                    _createElementVNode("span", _hoisted_3, _toDisplayString(item.name), 1)
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(Col), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Radio), {
                      value: item.id,
                      checked: channelId.value === item.id
                    }, null, 8, ["value", "checked"])
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 2
            }, 1032, ["onClick"])
          ]))
        }), 128))
      : _createCommentVNode("", true),
    _createVNode(_unref(Button), {
      type: "primary",
      class: "to-pay-but",
      loading: loadingPaid.value,
      disabled: __props.isTimeout,
      onClick: _cache[0] || (_cache[0] = ($event: any) => (toPay()))
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(__props.isTimeout ? '订单已超时' : '确认支付'), 1)
      ]),
      _: 1
    }, 8, ["loading", "disabled"])
  ]))
}
}

})